import { YoutubeProcessor } from '../../models/media/youtube.processor';
import { EVENTS } from '../events';
import { STATES } from '../states';
import { PROVIDERS } from '../providers';
import { GoMediaPlayer } from './media-player';
import type { PlaylistItem } from '../playlist-item';
import { nextAnimationFrame } from '../../next-animation-frame/next-animation-frame';

const _window = (window as any);

export enum YOUTUBE_STATES {
	UNSTARTED = -1,
	ENDED = 0,
	PLAYING = 1,
	PAUSED = 2,
	BUFFERING = 3
}

export const youtubeStateMap = {
	[YOUTUBE_STATES.UNSTARTED]: STATES.IDLE,
	[YOUTUBE_STATES.ENDED]: STATES.COMPLETE,
	[YOUTUBE_STATES.PLAYING]: STATES.PLAYING,
	[YOUTUBE_STATES.PAUSED]: STATES.PAUSED,
	[YOUTUBE_STATES.BUFFERING]: STATES.BUFFERING
};

export interface YoutubePlayerOptions {
	width: string;
	height: string;
	url: string;
}

export class YoutubePlayerAdapter extends GoMediaPlayer {

	public static loading: Promise<void> = null;

	private captionsOn: boolean = false;
	private reinitPromise: Promise<void> = null;

	private constructor (
		private youtubePlayer: any,
		private elementId: string,
		private options: YoutubePlayerOptions
	) {
		super();
	}

	public static async setup (elementId: string, options: YoutubePlayerOptions): Promise<YoutubePlayerAdapter> {
		await YoutubePlayerAdapter.waitForYoutubeAPIReady();

		const youtubePlayer = YoutubePlayerAdapter.createPlayer(elementId, options, 0);

		return new Promise((resolve) => {
			youtubePlayer.addEventListener('onReady', () => {
				const youtubePlayerAdapter = new YoutubePlayerAdapter(youtubePlayer, elementId, options);
				youtubePlayerAdapter.initEventEmulation();
				youtubePlayerAdapter.initEventAdapter();
				resolve(youtubePlayerAdapter);
			});
		});
	}

	public static waitForYoutubeAPIReady (): Promise<void> {

		if (this.loading) {
			return this.loading;
		}

		return this.loading = new Promise ((resolve) => {
			const tag = document.createElement('script');
			tag.src = 'https://www.youtube.com/iframe_api';
			tag.id = 'youtube-script';
			// cannot set tag.crossOrigin = 'anonymous' here unfortunately
			_window.onYouTubeIframeAPIReady = () => {
				resolve();
			};
			document.head.prepend(tag);
		});
	}

	private static createPlayer (
		elementId: string,
		options: YoutubePlayerOptions,
		ccLoadPolicy: 0 | 1
	): any {
		return new _window.YT.Player(elementId, {
			width: options.width,
			height: options.height,
			videoId: YoutubeProcessor.getYoutubeId(options.url),
			playerVars: {
				controls: 0,
				autoplay: 0,
				disablekb: 1,
				modestbranding: 1,
				iv_load_policy: 3,
				cc_load_policy: ccLoadPolicy,
				showinfo: 0,
				rel: 0,
				start: 0,
				playsinline: 1
			}
		});
	}

	public play (): void {
		this.emit(EVENTS.REQUEST_PLAY);
		this.youtubePlayer.playVideo();
	}

	public pause (): void {
		this.emit(EVENTS.REQUEST_PAUSE);
		this.youtubePlayer.pauseVideo();
	}

	public stop (): void {
		this.youtubePlayer.stopVideo();
	}

	public getTime (): number {
		return Math.floor(this.youtubePlayer.getCurrentTime() * 1000);
	}

	public getDuration (): number {
		return this.youtubePlayer.getDuration() * 1000;
	}

	public getElement (): HTMLIFrameElement {
		return this.youtubePlayer.getIframe();
	}

	public destroy (): void {
		super.destroy();
		this.youtubePlayer.destroy();
	}

	public getMute (): boolean {
		return this.youtubePlayer.isMuted();
	}

	public setMute (value: boolean): Promise<void> {
		if (value) {
			this.youtubePlayer.mute();
		} else {
			this.youtubePlayer.unMute();
		}
		return nextAnimationFrame().then(() => nextAnimationFrame());
	}

	public setVolume (value: number): void {
		this.youtubePlayer.setVolume(value);
	}

	public getVolume (): number {
		return this.youtubePlayer.getVolume();
	}

	public isPlaybackRateSupported (): boolean {
		return true;
	}

	public get playbackRate (): number {
		return this.youtubePlayer.getPlaybackRate();
	}

	public set playbackRate (value: number) {
		this.youtubePlayer.setPlaybackRate(value);
	}

	public captionsEnabled (): boolean {
		return this.captionsOn;
	}

	public toggleCaptions (value?: boolean): void {
		const nextValue = value == null ? !this.captionsOn : value;
		if (nextValue === this.captionsOn) {
			return;
		}

		this.captionsOn = nextValue;
		void this.reinitPlayer(this.captionsOn ? 1 : 0);
	}

	public hasCaptions (): boolean {
		return false;
	}

	public getPlaylistItem (): PlaylistItem {
		return {
			file: this.youtubePlayer.getVideoUrl()
		};
	}

	public getProvider (): {name: PROVIDERS} {
		return { name: PROVIDERS.YOUTUBE };
	}

	protected seekTo (time: number /* in milliseconds */): void {
		this.youtubePlayer.seekTo(time / 1000);
	}

	private initEventAdapter (): void {
		this.youtubePlayer.addEventListener('onStateChange', () => {
			this.handlePlayerStateChange();
		});
	}

	private handlePlayerStateChange (): void {
		const youtubeState: number = this.youtubePlayer.getPlayerState();
		const state: STATES = youtubeStateMap[youtubeState] || STATES.IDLE;
		this.setState(state);
	}

	private reinitPlayer (ccLoadPolicy: 0 | 1): Promise<void> {
		if (this.reinitPromise) {
			return this.reinitPromise;
		}

		const currentTime = this.youtubePlayer.getCurrentTime() || 0;
		const state = this.getState();
		const shouldPlay = state === STATES.PLAYING || state === STATES.BUFFERING;
		const wasMuted = this.youtubePlayer.isMuted() === true;
		const volume = this.youtubePlayer.getVolume();
		const playbackRate = this.youtubePlayer.getPlaybackRate();
		const iframe = this.youtubePlayer.getIframe();
		const parent = iframe?.parentElement;
		const nextSibling = iframe?.nextSibling;

		this.youtubePlayer.destroy();

		if (parent) {
			const container = document.createElement('div');
			container.id = this.elementId;
			parent.insertBefore(container, nextSibling);
		}

		this.reinitPromise = new Promise((resolve) => {
			const youtubePlayer = YoutubePlayerAdapter.createPlayer(this.elementId, this.options, ccLoadPolicy);
			youtubePlayer.addEventListener('onReady', () => {
				this.youtubePlayer = youtubePlayer;
				this.initEventAdapter();

				if (typeof playbackRate === 'number') {
					this.youtubePlayer.setPlaybackRate(playbackRate);
				}
				if (typeof volume === 'number') {
					this.youtubePlayer.setVolume(volume);
				}
				if (wasMuted) {
					this.youtubePlayer.mute();
				} else {
					this.youtubePlayer.unMute();
				}
				if (currentTime > 0) {
					this.youtubePlayer.seekTo(currentTime);
				}
				if (shouldPlay) {
					this.youtubePlayer.playVideo();
				} else if (state === STATES.PAUSED) {
					this.youtubePlayer.pauseVideo();
				}

				this.reinitPromise = null;
				resolve();
			});
		});

		return this.reinitPromise;
	}
}
